import { APIClient, HTTPClientConfig } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { Logger } from '../support/Logger';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { nanoid } from 'nanoid';
import { ISO8601 } from '../support/ISO8601';
import { APP_FOREGROUNDED, APP_BACKGROUNDED } from '../AppContainer';
import { isPlatform } from '@ionic/react';

export interface IMessageFromSocket {
    action: string;
};

export interface IResourceChangedSocketMessage extends IMessageFromSocket {
    action: 'resourceChanged';
    resourceToRefresh: string;
    resourceChangeType: 'changed' | 'deleted';
};

export interface IPingAckMessage extends IMessageFromSocket {
    action: 'ping-ack';
    messageId: string;
};

const MAX_BACKOFF_SECONDS = 5 * 60; // 5 minutes
const PING_INTERVAL_MS = 10 * 1000; // 10 seconds

// native only: only rebuild the socket on foreground if the app was backgrounded at least this long.
// A brief interruption (Control Center, a permission/biometric prompt) fires a quick background->
// foreground pair that doesn't kill the socket, so rebuilding on it would just churn the connection.
const REBUILD_AFTER_BACKGROUND_MS = 10 * 1000; // 10 seconds

export class SocketState extends State<Record<string, never>> {
    static inject = () => [
        APIClient,
        Logger,
        HTTPClientConfig,
        BufferedEventBus
    ];
    constructor(
        private apiClient: APIClient,
        private logger: Logger,
        private httpConfig: HTTPClientConfig,
        private events: BufferedEventBus
    ){
        super({ });

        // Only NATIVE needs proactive foreground/background socket management. Backgrounding a native
        // app can leave a half-dead socket (client readyState still OPEN while the server has dropped
        // it), so on a real return-to-foreground we rebuild (close()+connect()), and on background we
        // stop pinging (battery). The rebuild is debounced on how long we were backgrounded: a brief
        // interruption (Control Center, a prompt) leaves the socket alive, so we just resume the
        // keepalive rather than churn a fresh connection on every quick background->foreground pair.
        //
        // On WEB we do neither: the socket stays alive across tab visibility changes, and
        // APP_FOREGROUNDED fires on EVERY visibilitychange — rebuilding a healthy web socket on each
        // one churns the connection (a tab whose visibility flaps reconnects every cycle, piling up
        // connection records). A genuinely-closed web socket self-heals via the close handler's
        // backoff reconnect, and the ForegroundReconciler catch-up still runs on web to converge state.
        this.events.on(APP_FOREGROUNDED, () => {
            if (!isPlatform('capacitor')) {
                return;
            }
            if (!this.webSocketUrl) {
                // init() never found a websocket url (or hasn't run yet); nothing to rebuild
                return;
            }
            const backgroundedMs = this.backgroundedAt ? Date.now() - this.backgroundedAt : Infinity;
            this.backgroundedAt = undefined;
            if (backgroundedMs < REBUILD_AFTER_BACKGROUND_MS) {
                // brief interruption — the socket is almost certainly still alive, so don't churn it.
                // resume the keepalive we stopped on background, but only if still connected; if a
                // reconnect is already in flight, its open handler will restart pinging.
                if (this.getConnectionStatus() === 'connected') {
                    this.debugLog('SocketState -- APP_FOREGROUNDED after brief interruption, resuming pinging');
                    this.stopPinging();
                    this.startPinging();
                }
                return;
            }
            this.debugLog('SocketState -- APP_FOREGROUNDED, rebuilding socket');
            this.close();
            this.connect();
        });

        this.events.on(APP_BACKGROUNDED, () => {
            if (!isPlatform('capacitor')) {
                return;
            }
            this.backgroundedAt = Date.now();
            this.debugLog('SocketState -- APP_BACKGROUNDED, stopping pinging');
            this.stopPinging();
        });
    }

    socket?: WebSocket;
    socketCount = 0;
    webSocketUrl?: string;
    backOffSeconds = 1;
    logs: string = '';
    debug = false;

    // native only: when the app was last backgrounded, so APP_FOREGROUNDED can debounce the rebuild
    private backgroundedAt?: number;

    private debugLog = (message: string) => {
        if (this.debug) {
            this.logs = this.logs + `${new Date().toLocaleTimeString()} ${message}\n`;
            this.logs = this.logs.split('\n').reverse().slice(0,250).reverse().join('\n');
            this.logger.info(message);
            this.setState({ });
        }
    };

    getConnectionStatus = (): 'connecting' | 'connected' | 'disconnected' | 'disconnecting' => {
        // https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
        if (this.socket && this.socket.readyState === 1){
            return 'connected';
        }
        if (this.socket && this.socket.readyState === 0){
            return 'connecting';
        }
        if (this.socket && this.socket.readyState === 2){
            return 'disconnecting';
        }
        if (this.socket && this.socket.readyState === 3){
            // now the socket considers itself closed, but
            // we expect the socket to be undefined, so our
            // state is that we're still disconnecting it
            return 'disconnecting';
        }
        else if (this.socket){
            // this should never happen b/c it should always have a
            // readyState (eg readyState: 0, connecting)
            return 'connecting';
        }
        else {
            return 'disconnected';
        }
    };

    getPrototocol = () => {
        return this.socket?.protocol || '';
    };

    private logError = (message: string) => {
        this.logs = this.logs + `${new Date().toLocaleTimeString()} ${message}\n`;
        this.logger.error(message);
        this.setState({ });
    };

    init = async () => {
        try {
            const entry = await this.apiClient.entry();

            this.webSocketUrl = entry.links.websockets?.href;

            if (!this.webSocketUrl){
                this.debugLog('SocketState.init -- no websocket url in entry resource so aborting socket init');
                return;
            }

            this.connect();
        }
        catch (err: any){
            this.logError(`SocketState.init -- failed to init. Will not try again til next app load. Error: ${err.toString()}`);
        }
    };

    connect = () => {
        this.lastFivePings = [];
        this.preventRetry = false;

        if (!this.webSocketUrl){
            this.debugLog('SocketState.connect -- no websocket url in entry resource so aborting');
            return;
        }

        this.socketCount++;
        this.socket = new WebSocket(this.webSocketUrl,
            // when adding a new protocol, cannot contain " " or ":"
            [
                // this is the protocol, used for content negotation
                // we should avoid it, but if necessary to make a breaking change
                // we do so by agreeing to this protocol (the server has to respond with the same protocol)
                'ih-sockets-v1',

                // auth in websockets is permanently immature
                // while there are a few acceptable ways, using the protocol is the best IMO (and others)
                `jwt__${this.httpConfig.accessToken}`
            ],
        );

        this.debugLog('SocketState.connect -- connecting');

        this.socket.addEventListener('open', () => {
            // next time we try and connect start at 1s backoff
            this.backOffSeconds = 1;

            this.debugLog('SocketState.connect -- event open');

            this.startPinging();

            // tell the reconcile layer we have a live connection so it can run a
            // catch-up reload — covers messages missed while disconnected/backgrounded
            this.events.emit('web-sockets.connected');
        });

        this.socket.addEventListener('message', (event) => {
            this.debugLog(`SocketState -- event message ${event.data}`);

            let message: IMessageFromSocket | undefined;

            try {
                message = JSON.parse(event.data);
            }
            catch (err){
                // do nothing, undefiend message will be handled below
            }

            if (!message){
                this.logError(`SocketState -- unexpected message, likely failed to parse JSON from string "${event.data}"`);
            }
            else if (message.action === 'resourceChanged'){
                this.events.emit('web-sockets.message', JSON.parse(event.data));
            }
            else if (message.action === 'ping-ack'){
                // do nothing
                const ping = this.lastFivePings.find(p => p.messageId === (message as IPingAckMessage).messageId);
                if (ping){
                    ping.ackedAt = new Date().toISOString();
                }
            }
            else {
                this.debugLog(`SocketState -- unexpected message ${JSON.stringify(message)}`);
            }
        });

        this.socket.addEventListener('error', (event) => {
            // we don't get a meaningful error if the connection wasn't established
            // b/c we're not considered a trusted user agent
            // https://stackoverflow.com/questions/31002592/javascript-doesnt-catch-error-in-websocket-instantiation
            // so this error is mostly worthless
            // and we can't use it to determine if the erorr is network related (user problem / ignore)
            // or release / configuration related (escalate to developer / sentry)
            this.debugLog(`SocketState -- event error. ${JSON.stringify(event)}`);
        });

        this.socket.addEventListener('close', () => {
            // note socket is closed when never opened (eg failed to negotiate protocol, auth, bad network)
            // it is obviously also called when the connection is closed after its been opened
            //  - eg browser closes it
            //  - or we close it (eg debug page)
            // the browser does not close it very aggressively, so usually its going to be closed because
            // it failed to negotiate (never opened)
            // or b/c we closed it (failed pings)

            this.debugLog(`SocketState event -- close`);
            this.socket = undefined;

            if (!this.preventRetry){
                this.backoffAndRetryConnect();
            }
        });

        this.debugLog('SocketState.connect -- sync return');
    };

    private backoffAndRetryConnect = () => {
        Promise.resolve().then(async () => {
            this.debugLog(`backing off ${this.backOffSeconds} seconds`);
            await new Promise(resolve => setTimeout(resolve, this.backOffSeconds * 1000));

            // if we have to try again, wait longer
            this.backOffSeconds = this.backOffSeconds * 2;
            this.backOffSeconds = Math.min(this.backOffSeconds, MAX_BACKOFF_SECONDS);
            this.setState({ });

            // try again
            this.connect();
        });
    };

    private preventRetry = false;

    close = () => {
        this.preventRetry = true;
        if (this.socket){
            try {
                this.stopPinging();
                this.socket.close();
                this.socket = undefined;
                this.setState({ });
            }
            catch (err: any){
                this.debugLog(`failed to close socket with error ${err.string()}`);
            }
        }
    };

    private pingTimeoutId?: NodeJS.Timeout;
    lastFivePings: { messageId: string; sentAt: ISO8601; ackedAt?: ISO8601; }[] = [];
    private startPinging = () => {
        const ping = () => {
            if (this.socket && this.getConnectionStatus() === 'connected'){
                const msg = { action: 'ping', messageId: nanoid() };
                this.debugLog(`SocketState.send ${JSON.stringify(msg)}`);

                // this will not fail (in any way observable by the client) while "connected", even
                // if the connection is effectively closed (eg wifi is disabled)
                // we detect failure by looking for the ack from the server
                // we close the connection on 5 failed acks in a row (50 seconds)
                this.socket.send(JSON.stringify(msg));

                this.lastFivePings.push({
                    messageId: msg.messageId,
                    sentAt: new Date().toISOString(),
                });
                this.lastFivePings = this.lastFivePings.reverse().slice(0, 5).reverse();
                if (this.lastFivePings.length >= 5 && this.lastFivePings.every(p => !p.ackedAt)){
                    this.connectionFailedPing();
                }
            }
            else {
                // not stopping ping b/c something else shoudl do that, and don't want to add complexity of this also trying to close
                // when it is likely already closing perfectly fine
                this.debugLog(`not sending ping, not connected. Got ${this.getConnectionStatus()}`);
            }

            this.pingTimeoutId = setTimeout(ping, PING_INTERVAL_MS);
        };
        ping();
    };

    private connectionFailedPing = () => {
        // the socket is dead, destroy it
        if (this.socket){
            this.socket.close();
            this.socket = undefined;
        }

        // stop trying to ping
        this.stopPinging();

        // start exponential back off trying to reconnect
        this.connect();
    };

    private stopPinging = () => {
        if (this.pingTimeoutId){
            clearTimeout(this.pingTimeoutId);
            this.pingTimeoutId = undefined;
        }
    };
}
